'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CTAOption {
  id: string;
  icon: string;
  title: string;
  description: string;
  action: string;
  urgency: 'high' | 'medium' | 'low';
}

const FinalCTA = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const ctaOptions: CTAOption[] = [
    {
      id: 'urgent',
      icon: 'BoltIcon',
      title: 'Need Immediate Liquidation?',
      description: 'Business closure or urgent cash needs',
      action: 'Call Emergency Line: (555) 123-4567',
      urgency: 'high'
    },
    {
      id: 'planned',
      icon: 'CalendarIcon',
      title: 'Planning Fleet Transition?',
      description: 'Strategic timing for maximum value',
      action: 'Schedule Consultation',
      urgency: 'medium'
    },
    {
      id: 'exploring',
      icon: 'MagnifyingGlassIcon',
      title: 'Just Exploring Options?',
      description: 'Get a no-obligation valuation estimate',
      action: 'Request Quick Quote',
      urgency: 'low'
    }
  ];

  const getUrgencyStyles = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return 'bg-error/10 border-error/30 hover:bg-error/20';
      case 'medium':
        return 'bg-warning/10 border-warning/30 hover:bg-warning/20';
      case 'low':
        return 'bg-success/10 border-success/30 hover:bg-success/20';
      default:
        return 'bg-primary/10 border-primary/30 hover:bg-primary/20';
    }
  };

  const getUrgencyIconColor = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-primary';
    }
  };

  if (!isHydrated) {
    return (
      <section className="bg-gradient-to-br from-primary via-primary to-secondary py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              Choose Your Path Forward
            </h2>
            <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
              Whether you need immediate action or are planning ahead, we have the right solution for your fleet.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-br from-primary via-primary to-secondary py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
            Choose Your Path Forward
          </h2>
          <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
            Whether you need immediate action or are planning ahead, we have the right solution for your fleet.
          </p>
        </div>

        {/* CTA Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {ctaOptions.map((option) => (
            <div
              key={option.id}
              className={`rounded-xl p-6 border-2 transition-all duration-200 cursor-pointer ${getUrgencyStyles(option.urgency)}`}
            >
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="p-4 bg-primary-foreground/10 rounded-full">
                    <Icon
                      name={option.icon as any}
                      size={32}
                      variant="solid"
                      className={getUrgencyIconColor(option.urgency)}
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary-foreground mb-2">
                    {option.title}
                  </h3>
                  <p className="text-sm text-primary-foreground/80 mb-4">
                    {option.description}
                  </p>
                  <button className="w-full px-6 py-3 bg-primary-foreground text-primary font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-0.5 shadow-brand">
                    {option.action}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-8 border border-primary-foreground/20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <Icon name="PhoneIcon" size={32} variant="outline" className="text-accent mx-auto" />
              <p className="text-sm font-semibold text-primary-foreground">Call Us</p>
              <p className="text-lg font-bold text-primary-foreground">(555) 123-4567</p>
              <p className="text-xs text-primary-foreground/70">Mon-Fri 8AM-8PM EST</p>
            </div>
            <div className="space-y-2">
              <Icon name="EnvelopeIcon" size={32} variant="outline" className="text-accent mx-auto" />
              <p className="text-sm font-semibold text-primary-foreground">Email Us</p>
              <p className="text-lg font-bold text-primary-foreground">sales@fleetdirect.com</p>
              <p className="text-xs text-primary-foreground/70">24-hour response time</p>
            </div>
            <div className="space-y-2">
              <Icon name="MapPinIcon" size={32} variant="outline" className="text-accent mx-auto" />
              <p className="text-sm font-semibold text-primary-foreground">Visit Us</p>
              <p className="text-lg font-bold text-primary-foreground">123 Fleet Avenue</p>
              <p className="text-xs text-primary-foreground/70">Atlanta, GA 30303</p>
            </div>
          </div>
        </div>

        {/* Trust Reinforcement */}
        <div className="mt-8 text-center">
          <div className="flex flex-wrap justify-center items-center gap-6">
            <div className="flex items-center space-x-2">
              <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm text-primary-foreground/80">BBB A+ Rated</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="LockClosedIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm text-primary-foreground/80">Secure Transactions</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="DocumentCheckIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm text-primary-foreground/80">Licensed & Bonded</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="StarIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm text-primary-foreground/80">4.9/5 Client Rating</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;